<script setup lang="ts">
import { computed } from "vue";
import type { ICatalogBadgeProps, ICatalogBadge } from "../types";
import VLabel from "@magnit/core/src/components/VLabel/VLabel.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";

const props = withDefaults(defineProps<ICatalogBadgeProps>(), {
  labelSize: "s",
  iconPosition: "right",
  iconSize: 20,
});
const emit = defineEmits<{
  "click:badge": [badge: ICatalogBadge];
}>();

const text = computed(() => props.badge.text);
const textColor = computed(() => props.badge.textColor);
const arrow = computed(() => !!props.badge.description && !props.hideArrow);
const backgroundGradient = computed(() => {
  const gradient = props.badge.backgroundGradient;
  if (!gradient) return null;
  return `var(--pl-${gradient.toLocaleLowerCase().split(/[ /]/gi).join("-")})`;
});
const backgroundColor = computed(() => {
  if (backgroundGradient.value) return backgroundGradient.value;
  return props.badge.backgroundColor;
});
const icon = computed(() => {
  const icon = props.badge.backgroundIcon;
  if (!icon) return null;
  const iconArr = icon.toLocaleLowerCase().split(/[ /]/gi);
  const project = iconArr[0] as "base" | "v1";
  const name = iconArr
    .filter((i) => !!i)
    .slice(1)
    .join("-");
  return { project, name, size: props.iconSize };
});
const displayIcon = computed(() => icon.value !== null);
const displayIconLeft = computed(
  () => displayIcon.value && props.iconPosition === "left",
);
const displayIconRight = computed(
  () => displayIcon.value && props.iconPosition === "right",
);

const onBadgeClick = () => emit("click:badge", props.badge);
</script>

<template>
  <VLabel
    class="catalog-badge"
    :size="labelSize"
    :text-color="textColor"
    :bg-color="backgroundColor"
    :arrow="arrow"
    @click="onBadgeClick"
  >
    <slot v-if="displayIconLeft" name="iconLeft">
      <VIcon v-bind="icon" />
    </slot>
    {{ text }}
    <slot v-if="displayIconRight" name="iconRight">
      <VIcon v-bind="icon" />
    </slot>
  </VLabel>
</template>
